import { checkFileExists } from "@/views/course-creation/api-client/courseInfoAPI";
import { io } from "socket.io-client";
import { Sha256 } from "@/utils/sha256";

export default {
	data() {
		return {
			videoFile: false,
		};
	},
	methods: {
		async calculateHash(
			file,
			{ bucket, assetPath },
			fileIndex,
			fileDataCopy,
			isVideo
		) {
			return new Promise((resolve, reject) => {
				(async () => {
					try {
						let fileHash;
						// Use native crypto for files < 1GB for speed
						if (file.size < 1024 * 1024 * 1024) {
							const arrayBuffer = await new Promise((res, rej) => {
								const reader = new FileReader();
								reader.readAsArrayBuffer(file);
								reader.onload = (e) => res(e.target.result);
								reader.onerror = (e) => rej(e);
							});
							const hash = await crypto.subtle.digest("SHA-256", arrayBuffer);
							const hashArray = Array.from(new Uint8Array(hash));
							fileHash = hashArray
								.map((b) => b.toString(16).padStart(2, "0"))
								.join("");
						} else {
							// Use streaming SHA-256 for large files
							const sha256 = new Sha256();
							const chunkSize = 20 * 1024 * 1024; // 20MB chunks
							let offset = 0;
							while (offset < file.size) {
								const end = Math.min(offset + chunkSize, file.size);
								const blob = file.slice(offset, end);
								const buffer = await new Promise((res, rej) => {
									const reader = new FileReader();
									reader.readAsArrayBuffer(blob);
									reader.onload = (e) => res(e.target.result);
									reader.onerror = (e) => rej(e);
								});
								sha256.update(buffer);
								offset = end;
							}
							fileHash = sha256.hex();
						}

						const payload = {
							fileName: file.name,
							content: fileHash,
							size: file.size,
							bucket,
							bucketPath: assetPath + "/" + file.name,
						};
						const response = await checkFileExists(payload);
						if (response.status === 200) {
							const bucketPath = await this.chunkAndSendFile(
								file,
								response.data.data,
								fileIndex,
								fileDataCopy,
								isVideo
							);
							console.log(bucketPath, "bucketPath");
							resolve(bucketPath);
						} else {
							reject(new Error("File does not exist in the bucket"));
						}
					} catch (error) {
						reject(error);
					}
				})();
			});
		},

		async chunkAndSendFile(
			file,
			uploadData,
			fileIndex,
			fileDataCopy,
			isVideo = false
		) {
			return new Promise((resolve, reject) => {
				try {
					const chunkSize = 5242880;
					let offset = 0;
					let startTime = null;
					let totalUploadedSize = 0;

					const socket = io("https://awsmultipart.hire3x.com/", {
						transports: ["websocket"],
						withCredentials: true,
					});
					const { _id, totalChunks, fileSize, uploadId, bucket, bucketPath } =
						uploadData;

					// Helper to read next chunk
					const readNextChunk = async () => {
						if (offset < file.size) {
							const end = Math.min(offset + chunkSize, file.size);
							const blob = file.slice(offset, end);
							const buffer = await new Promise((res, rej) => {
								const reader = new FileReader();
								reader.readAsArrayBuffer(blob);
								reader.onload = (e) => res(e.target.result);
								reader.onerror = (e) => rej(e);
							});
							offset = end;
							return buffer;
						}
						return null;
					};

					socket.on("connect", async () => {
						this["uploadVideoData"] = uploadData;

						this.uploadStatus = "Uploading...";
						this.isVideoUploading = true;
						startTime = Date.now();
						const acceptedVideoTypes = [
							".mov",
							".mp4",
							".webm",
							".MOV",
							".MP4",
							".WEBM",
						];
						const fileName = file.name.toLowerCase();
						const isAccepted = acceptedVideoTypes.some((ext) =>
							fileName.endsWith(ext)
						);
						this.videoFile = isAccepted;

						// Send first chunk
						const firstChunk = await readNextChunk();
						if (firstChunk) {
							socket.emit(
								"store-chunk",
								firstChunk,
								_id,
								uploadData.uploadedChunks,
								totalChunks,
								fileSize,
								uploadId,
								bucket,
								bucketPath,
								this.videoFile
							);
						}

						socket.on("send-chunk", async (percent, uploadedChunksCount) => {
							const currentTime = Date.now();
							const timeTaken = (currentTime - startTime) / 1000;
							startTime = currentTime;
							totalUploadedSize += chunkSize;
							const uploadSpeed = chunkSize / timeTaken;
							const remainingSize = fileSize - totalUploadedSize;
							const estimatedTimeRemaining = remainingSize / uploadSpeed;
							const formatTime = (seconds) => {
								const h = Math.floor(seconds / 3600);
								const m = Math.floor((seconds % 3600) / 60);
								const s = Math.floor(seconds % 60);

								const formattedParts = [];
								if (h > 0) formattedParts.push(`${h.toString()}h`);
								if (m > 0) formattedParts.push(`${m.toString()}min`);
								if (s > 0 || formattedParts.length === 0)
									formattedParts.push(`${s.toString()}sec`);

								return formattedParts.join(" ");
							};
							this.timeRemaining = formatTime(estimatedTimeRemaining);

							this.progress = parseInt(percent);
							if (this.viewProgress) {
								this.viewProgress[isVideo ? "previewVideo" : "thumbnail"] =
									parseInt(percent);
							}
							this.$store.dispatch("assetVUEX/updateUpload", {
								index: fileIndex,
								tempId: fileDataCopy?.tempId,
								progress: this.progress,
								timeRemaining: this.timeRemaining,
								id: this.uploadVideoData._id,
								estimatedTimeRemaining: estimatedTimeRemaining,
							});

							const nextChunk = await readNextChunk();
							if (nextChunk) {
								const stillExists =
									this.$store.state.assetVUEX.uploadedFiles?.some(
										(u) => u.fileData?.tempId === fileDataCopy?.tempId
									);

								if (
									stillExists ||
									!this.videoFile ||
									this.$route.name === "QuestionBank" ||
									this.$route.name === "coursecreation"
								) {
									socket.emit(
										"store-chunk",
										nextChunk,
										_id,
										uploadedChunksCount,
										totalChunks,
										fileSize,
										uploadId,
										bucket,
										bucketPath,
										this.videoFile
									);
								}
							}
						});
						socket.on("upload-completing", () => {
							this.progress = 100;
							if (this.viewProgress) {
								this.viewProgress[
									isVideo && this.videoFile ? "previewVideo" : "thumbnail"
								] = 100;
							}
							this.$store.dispatch("assetVUEX/updateUpload", {
								index: fileIndex,
								tempId: fileDataCopy?.tempId,
								progress: this.progress,
								timeRemaining: "",
								id: this.uploadVideoData._id,
							});
						});

						const url =
							"https://training-api.hire3x.com/common/change-extension/";
						socket.on("upload-complete", async () => {
							// socket.disconnect(); not needed from  backend it is handled
							this.uploadStatus = "Completing upload";

							if (!isVideo || this.$options.name === "CourseInformation") {
								const uploadResult = await this.saveVideoAsset(
									bucketPath,
									file,
									fileDataCopy
								);

								const referenceID =
									uploadResult?.assetID ||
									uploadResult?.questionKey ||
									this.$route?.query?.courseID;
								const item = uploadResult?.questionKey
									? 3
									: this.$route?.query?.tab === "structure" ||
										this.$route.name === "assetbank"
										? 1
										: 2;

								if (this.videoFile) {
									socket.emit(
										"finish-register-callback",
										bucket,
										bucketPath,
										uploadData._id,
										url,
										"POST",
										{
											filePath: bucketPath,
											referenceID: referenceID,
											item: item,
										}
									);
								}
							} else {
								resolve(bucketPath);
							}
						});

						socket.on("upload-failed", () => {
							socket.disconnect();
							reject(new Error("Upload failed"));
						});
					});
					if (this.$options.name !== "CourseInformation") {
						this.socket = socket;
					}
					this.$store.commit("assetVUEX/setUploadSocket", socket);
				} catch (error) {
					reject(error);
				}
			});
		},
		async readFileChunk(blob) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.readAsArrayBuffer(blob);
				reader.onload = (event) => {
					resolve(event.target.result);
				};
				reader.onerror = (error) => {
					reject(error);
				};
			});
		},
	},
};
